import { useQuery } from '@tanstack/react-query';
import { medicalProfileFilesService } from '../../primary-record-types/apiTypes';
import { getIntl } from '../../utils/intl';
import { getMedicalProfileFilePageAudioCacheKey } from './cacheKeys';
import { appAPI } from '../../utils/api';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import _uniqBy from 'lodash/uniqBy';

const intl = getIntl('useMedicalProfileFileAudio', {
    notFound: 'Error loading audio',
});

export type UseMedicalProfileFileAudioArgs = {
    medicalProfileId: string;
    medicalProfileFileId: string;
};

export type NoAudioResponse = {
    status: 'loading' | 'not-found';
    audio: null;
};

export type AudioResponse = {
    status: 'loaded';
    audio: medicalProfileFilesService.AudioFileDetails;
};

export type UseMedicalProfileFileAudioResponse = NoAudioResponse | AudioResponse;

export const useMedicalProfileFileAudio = ({
    medicalProfileId,
    medicalProfileFileId,
}: UseMedicalProfileFileAudioArgs): UseMedicalProfileFileAudioResponse => {
    const searchQuery = useQuery({
        queryKey: getMedicalProfileFilePageAudioCacheKey(medicalProfileId, medicalProfileFileId),
        queryFn: async () => {
            const response = await appAPI.get<APITypes.medicalProfileFilesService.GetAudioFileResponse>(
                urls.api.medicalProfileFiles.audio(medicalProfileId, medicalProfileFileId),
            );
            return response;
        },
        staleTime: 1000 * 60 * 5,
    });
    useQueryErrorNotify(searchQuery, intl('notFound'));

    if (searchQuery.isLoading || searchQuery.isStale || searchQuery.isFetching) {
        return { status: 'loading', audio: null };
    } else if (searchQuery.isError || !searchQuery.data?.audioDetails) {
        return { status: 'not-found', audio: null };
    } else {
        return { status: 'loaded', audio: searchQuery.data.audioDetails };
    }
};
